import React, { useCallback, useEffect, useState } from 'react';
import { AGGridHelix } from '@lwt-helix/ag-grid';
import { agGridComponents } from '../constants/gridColumns';
import { handleSortedHighlight } from '../../common/helpers/agGrid';
import { MAX_COUNT_SELECTED_ROWS } from '../helpers/helpers';
import { usePDFStore } from '../../store/pdf/store';
import { useSearchStore } from '../../store/store';
import { retainTerms as rt } from '../../common/components/table/agGrid/retainedSettings';
import { msTableViewNames } from '../constants';
import { normalizedModuleNames } from '../../constants';
import PropTypes from 'prop-types';

const ResultsTable = (props) => {
    const {
        columnDefs,
        rowData,
        gridRef,
        listingStatus,
        isShowSelectedRows,
        setSelectedRowsIds,
        showToast,
        noRowsComponent,
        noRowMessage,
        rank,
        hasHeaderTooltips,
        onSortChanged,
        getBottomRowSettings
    } = props;

    const [selectedData, setSelectedData] = useState([]);

    const {
        setPinnedBottomRowData,
    } = usePDFStore(state => ({
        setPinnedBottomRowData: state.setPinnedBottomRowData,
    }));

    const {
        restoreTableRetainedStateByType,
        handleFloatingFilterRetain,
        setTableRetainedStateByType,
        selectedModule,
    } = useSearchStore(state => ({
        restoreTableRetainedStateByType: state.restoreTableRetainedStateByType,
        handleFloatingFilterRetain: state.handleFloatingFilterRetain,
        setTableRetainedStateByType: state.setTableRetainedStateByType,
        selectedModule: state.selectedModule,
    }));

    const tableViewId = msTableViewNames[normalizedModuleNames[selectedModule]?.toLowerCase()];

    const handleGridReady = (e) => {
        if (!tableViewId) return;
        restoreTableRetainedStateByType({ api: e.api, tableId: tableViewId, type: rt.floatingFilter });
    };

    const handleFilterChanged = (e) => {
        if (!tableViewId) return;
        handleFloatingFilterRetain({ tableViewId, api: e.api, setTableRetainedStateByType });
    };

    const onFirstDataRendered = useCallback((params) => {
        params.api.forEachNode((node) => node.setSelected(!!node.data && node.data.isSelected));
        if (getBottomRowSettings) {
            gridRef.current.api.setPinnedBottomRowData(getBottomRowSettings());
            setPinnedBottomRowData(getBottomRowSettings());
        }
        if (setSelectedRowsIds) {
            const initialCheckedRows = params.api.getSelectedRows().map(row => {
                return row.officeId || row.brokerId || row.areaId || row.groupId;
            });
            setSelectedRowsIds(initialCheckedRows);
        }
    }, []);

    const pinnedBottomRowData = getBottomRowSettings && getBottomRowSettings();
    
    useEffect(() => {
        if (!pinnedBottomRowData) return;
        setPinnedBottomRowData(pinnedBottomRowData);
    }, [columnDefs]);

    useEffect(() => {
        const revisedData = isShowSelectedRows ? rowData?.filter(row => row.isSelected) : rowData;
        if (JSON.stringify(selectedData) !== JSON.stringify(revisedData)) setSelectedData(revisedData);
    }, [isShowSelectedRows, rowData]);

    const context = {
        maxRows: MAX_COUNT_SELECTED_ROWS,
        setSelectedRowsIds,
        showToast,
        listingStatus,
        listingView: rank,
        hasHeaderTooltips
    };

    return (
        <AGGridHelix
            ref={gridRef}
            rowData={selectedData}
            columnDefs={columnDefs}
            gridOptions={{context}}
            onSortChanged={(e) => {
                onSortChanged && onSortChanged(e);
                handleSortedHighlight(e);
            }}
            onComponentStateChanged={handleSortedHighlight}
            onFirstDataRendered={onFirstDataRendered}
            onGridReady={handleGridReady}
            onFilterChanged={handleFilterChanged}
            suppressCsvExport={false}
            components={agGridComponents}
            noRowsOverlayComponent={noRowsComponent}
            noRowsOverlayComponentParams={noRowMessage}
            pinnedBottomRowData={pinnedBottomRowData}
            suppressPropertyNamesCheck={true}
        />
    );
};

ResultsTable.propTypes = {
    columnDefs: PropTypes.array,
    rowData: PropTypes.array,
    gridRef: PropTypes.object,
    listingStatus: PropTypes.string,
    isShowSelectedRows: PropTypes.bool,
    setSelectedRowsIds: PropTypes.func,
    showToast: PropTypes.func,
    noRowsComponent: PropTypes.elementType,
    noRowMessage: PropTypes.object,
    rank: PropTypes.string,
    hasHeaderTooltips: PropTypes.bool,
    onSortChanged: PropTypes.func,
    getBottomRowSettings: PropTypes.func,
};

export default ResultsTable;
